import React, { useState } from 'react';
import Badge from 'atoms/Badge';
import Table from 'molecules/Table';
import DropdownV2 from 'molecules/Dropdown';
import { statusColors } from 'helper/requisitions/constants';
import { firmInitiativesStatusUILabel } from 'helper/firmInitiatives/constants';
import Link from 'atoms/Link';
import Text from 'atoms/Text';
import Tooltip from 'atoms/Tooltip';
import { RenderText } from '@/components/PractitionerAllocatedView';
import AlertModal from 'molecules/AlertModal';

export const PopupModalTrigger = ({ label, valueArray, title, allValues = [] }) => {
    const [showModal, setShowModal] = useState(false);

    return (
        <>
            <div className="">
                <p>
                    {label}
                    {valueArray?.length > 0 && (
                        <span
                            className="cursor-pointer underline underline-offset-[3px] text-green-900 pl-2"
                            onClick={() => setShowModal(true)}>+{valueArray?.length}</span>
                    )}

                </p>
            </div>

            <AlertModal
                show={showModal}
                title={title}
                description=""
                onSave={() => setShowModal(false)}
                cancelLabel="Close"
                onClose={() => setShowModal(false)}
                isShowSave={false}
            >
                <div className="grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto w-full px-2">
                    {allValues?.map((value, index) => (
                        <div key={index} className="flex items-center gap-2">
                            <span className="text-green-700 mt-1">•</span>
                            <Text variant="body" className="leading-normal">
                                {value}
                            </Text>
                        </div>
                    ))}
                </div>
            </AlertModal>
        </>
    );
};

export const PopupModalForDescription = ({ description, sliceLength = 75 }) => {
    if(!description) return "-"
    const [showModal, setShowModal] = useState(false);

    const label = description.slice(0, sliceLength)

    return (
        <>
            <div className="">
                <p>
                    {label}
                    <span className='text-green-900 cursor-pointer' onClick={() => setShowModal(true)}>...view more</span>
                </p>
            </div>

            <AlertModal
                show={showModal}
                title={'Descriptions'}
                description=""
                onClose={() => setShowModal(false)}
                isShowSave={false}
                isFooter= {false}
            >
                <div className=" max-h-[300px] overflow-y-auto w-full px-2 pb-5 mb-4 whitespace-pre-line">
                    {description}
                </div>
            </AlertModal>
        </>
    );
};


const FirmInitiativeListing = ({
    firmInitiativeRmAdminDataModel = {},
    showLoading = false
}) => {
    const actions = [
        {
            label: "Edit",
            onSelectedItem: (row) => firmInitiativeRmAdminDataModel.handleUpdate('edit', row),
        },
        {
            label: "Close ",
            onSelectedItem: (row) => firmInitiativeRmAdminDataModel.handleClose('close', row),
        },
        {
            label: "Delegate",
            onSelectedItem: (row) => firmInitiativeRmAdminDataModel.handleDelegate('delegate', row),
        },
    ];

    const columns = [
        {
            Header: 'Initiative Title',
            accessor: (row) => {
                const href = row?.id ? `/firm-initiatives/${row.id}?manager=true` : '#';
                return (
                    <Link
                        href={href}
                        className="block cursor-pointer"
                    >
                        <div className="line-clamp-2 whitespace-normal font-semibold">
                            {row.initiativeTitle}
                        </div>

                        <Text variant='body'>
                            Id: {row.initiativeId}
                        </Text>
                    </Link>
                )
            },
            cellwidth: 200,
            cardClass: 'col-span-2'
        },
        {
            Header: ' Status',
            accessor: (row) => (
                <Badge
                    color={statusColors[row.status?.toLowerCase()]}
                    label={firmInitiativesStatusUILabel[row.status]}
                    className="w-fit capitalize whitespace-nowrap"
                />
            ),
            cellwidth: 100,
            tdClass: 'frozenItem !left-[216px] noBorder',
            thClass: 'frozenItem !left-[216px] noBorder'
        },
        { Header: 'Open Firmwide', accessor: 'openFirmwide', cellwidth: 120 },
        { Header: 'Number of Openings', accessor: 'numberOfOpenings', cellwidth: 150 },
        { Header: 'Employees Interested', accessor: 'interestedEmployees', cellwidth: 150 },
        { Header: 'Start date', accessor: 'startDate', cellwidth: 120 },
        { Header: 'End date', accessor: 'endDate', cellwidth: 120 },
        { Header: 'Approx. Hours/days', accessor: 'hoursPerDay', cellwidth: 120 },
        {
            Header: 'Grade',
            accessor: (row) => {
                if (row.hasAllGrade) {
                    return 'All'
                }
                const gradeArray = row?.grades?.slice(1).map(deg => deg.label);
                const gradeLabel = row?.grades?.[0]?.label ?? '-';
                const allGradeValues = row?.grades?.map(deg => deg.label) ?? [];

                return (
                    <PopupModalTrigger
                        label={gradeLabel}
                        valueArray={gradeArray}
                        allValues={allGradeValues}
                        title="Grade"
                    />
                );
            },
            cellwidth: 150
        },
        {
            Header: 'Work location',
            accessor: (row) => {
                const locationArray = row?.locations?.slice(1).map(loc => loc.label);
                const locationLabel = row?.locations?.[0]?.label ?? '-';
                const allLocationValues = row?.locations?.map(loc => loc.label) ?? [];

                return (
                    <PopupModalTrigger
                        label={locationLabel}
                        valueArray={locationArray}
                        allValues={allLocationValues}
                        title="Location"
                    />
                );
            },
            cellwidth: 150
        },
        {
            Header: 'Description',
            accessor: (row) => {
                if(row?.description?.length < 75){
                    return <Text>{row.description}</Text>
                }
                return <PopupModalForDescription 
                    description={row.description}
                />
            },
            cellwidth: 250

        },
        {
            Header: 'Contacts',
            accessor: (row) => {
                const owners = row?.initiativeProjectOwner || [];

                if (!owners.length && !row.dlEmail) return "-";

                const format = (o) => `${o.name} (${o.empCode})`;

                if (!owners.length) {
                    return <Text>{row.dlEmail}</Text>;
                }

                const label = format(owners[0]);
                const valueArray = owners.slice(1).map(format);
                const allContactValues = owners.map(format);
                if(row.dlEmail){
                    valueArray.push(row.dlEmail);
                    allContactValues.push(row.dlEmail);
                }

                return (
                    <PopupModalTrigger
                        label={label}
                        valueArray={valueArray}
                        allValues={allContactValues}
                        title="Contacts"
                    />
                );
            },
            cellwidth: 150
        },
        {
            Header: 'Entity',
            accessor: (row) => {
                const entityArray = row?.entities?.slice(1).map(ent => ent.label);
                const entityLabel = row?.entities?.[0]?.label ?? '-';
                const allEntityValues = row?.entities?.map(ent => ent.label) ?? [];

                return (
                    <PopupModalTrigger
                        label={entityLabel}
                        valueArray={entityArray}
                        allValues={allEntityValues}
                        title="Entity"
                    />
                );
            },
            cellwidth: 150
        },
        {
            Header: 'Skill Set',
            accessor: (row) => {
                const skillSetArray = row?.skillSets?.slice(1).map(ss => ss.label);
                const skillSetLabel = row?.skillSets?.[0]?.label ?? '-';
                const allSkillSetValues = row?.skillSets?.map(ss => ss.label) ?? [];

                return (
                    <PopupModalTrigger
                        label={skillSetLabel}
                        valueArray={skillSetArray}
                        allValues={allSkillSetValues}
                        title="Skill Set"
                    />
                );
            },
            cellwidth: 150
        },
        {
            Header: 'Function',
            accessor: (row) => {
                const functionArray = row?.functions?.slice(1).map(f => f.name);
                const functionLabel = row?.functions?.[0]?.name ?? '-';
                const allFunctionValues = row?.functions?.map(f => f.name) ?? [];

                return (
                    <PopupModalTrigger
                        label={functionLabel}
                        valueArray={functionArray}
                        allValues={allFunctionValues}
                        title="Function"
                    />
                );
            },
            cellwidth: 150
        },
        {
            Header: 'Service Area',
            accessor: (row) => {
                const serviceAreaArray = row?.serviceAreas?.slice(1).map(sa => sa.name);
                const serviceAreaLabel = row?.serviceAreas?.[0]?.name ?? '-';
                const allServiceAreaValues = row?.serviceAreas?.map(sa => sa.name) ?? [];

                return (
                    <PopupModalTrigger
                        label={serviceAreaLabel}
                        valueArray={serviceAreaArray}
                        allValues={allServiceAreaValues}
                        title="Service Area"
                    />
                );
            },
            cellwidth: 150
        },
        {
            Header: 'Sub-Service Area',
            accessor: (row) => {
                const subServiceAreaArray = row?.subServiceAreas?.slice(1).map(sa => sa.name);
                const subServiceAreaLabel = row?.subServiceAreas?.[0]?.name ?? '-';
                const allSubServiceAreaValues = row?.subServiceAreas?.map(sa => sa.name) ?? [];

                return(
                     <PopupModalTrigger
                        label={subServiceAreaLabel}
                        valueArray={subServiceAreaArray}
                        allValues={allSubServiceAreaValues}
                        title="Sub-Service Area"
                    />
                )
            },
            cellwidth: 150
        },
        {
            Header: 'Sub-Sub Function',
            accessor: (row) => {
                const subSubFunctionArray = row?.subSubFunctions?.slice(1).map(ssf => ssf.name);
                const subSubFunctionLabel = row?.subSubFunctions?.[0]?.name ?? '-';
                const allSubSubFunctionValues = row?.subSubFunctions?.map(ssf => ssf.name) ?? [];

                return(
                     <PopupModalTrigger
                        label={subSubFunctionLabel}
                        valueArray={subSubFunctionArray}
                        allValues={allSubSubFunctionValues}
                        title="Sub-Sub Function"
                    />
                )
            },
            cellwidth: 150
        },
        {
            Header: 'Created By',
            accessor: (row) => (<RenderText
                title={row.createdBy}
                subTitle={row.createdOn}
            />),
            cellwidth: 150
        },
        {
            Header: 'Updated By',
            accessor: (row) => (<RenderText
                    title={row.updatedBy}
                    subTitle={row.updatedOn}
                />),
            cellwidth: 150
        },
        {
            Header: "Action",
            accessor: (row) => (
                <DropdownV2
                    zIndex="z-[1000]"
                    size="m"
                    dropdownItems={actions}
                    label='Actions'
                    isDisabled={row.status === 'closed' || !row.initiatorAccess}
                    onChange={(item) => item.onSelectedItem(row)}
                />
            ),
        },
    ];

    return (
        <>
            {firmInitiativeRmAdminDataModel.table.rows.length > 0 ? (
                <Table
                    columns={columns}
                    tableData={firmInitiativeRmAdminDataModel.table.rows}
                    pagination={{
                        ...firmInitiativeRmAdminDataModel.table.pagination,
                        onPageChange: firmInitiativeRmAdminDataModel.table.onPageChangeTable
                    }}
                    isDefaultTableCard
                    isStickyHeader
                    isSelection={false}
                    isPagination
                    className="md:max-h-[calc(100vh-14rem)] flex flex-col"
                    isFrozenleft
                    isFronzen
                    tableContainerClass="min-h-[250px]"
                />
            ) : (
                showLoading ? '' : 'Currently, there are no active firm initiative listings.'
            )}

        </>
    );
};

export default FirmInitiativeListing;
